import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { analyticsAPI, authAPI } from '../../services/api';
import './SpendingChart.css';

const SpendingChart = () => {
  const [spendingData, setSpendingData] = useState([]);
  const [totalSpent, setTotalSpent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Define colors for different categories
  const COLORS = {
    'Food & Dining': '#ef4444',
    'Transportation': '#f59e0b',
    'Housing': '#3b82f6',
    'Shopping': '#8b5cf6',
    'Groceries': '#10b981',
    'Utilities': '#06b6d4',
    'Entertainment': '#ec4899',
    'Transfer': '#6366f1',
    'Other': '#6b7280'
  };

  useEffect(() => {
    const fetchSpendingData = async () => {
      try {
        const user = authAPI.getCurrentUser();
        if (!user) return;

        const data = await analyticsAPI.getSpending(user.id);

        // Transform data for Recharts
        const chartData = data.spending.map(item => ({
          name: item.category || 'Other',
          value: parseFloat(item.total_amount),
          count: parseInt(item.transaction_count)
        }));

        setSpendingData(chartData);
        setTotalSpent(data.totalSpent || 0);
        setError('');
      } catch (err) {
        setError('Failed to load spending data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSpendingData();
  }, []);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-category">{payload[0].name}</p>
          <p className="tooltip-amount">${payload[0].value.toFixed(2)}</p>
          <p className="tooltip-count">{payload[0].payload.count} transactions</p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        className="chart-label"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="spending-chart">
      <div className="chart-header">
        <h2>Spending Analytics</h2>
        <p className="chart-subtitle">Category-wise spending breakdown</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {spendingData.length === 0 ? (
        <div className="no-data">
          <p>No spending data available</p>
        </div>
      ) : (
        <>
          <div className="total-spending-card">
            <div className="total-label">Total Spending</div>
            <div className="total-amount">${totalSpent.toFixed(2)}</div>
            <div className="total-subtitle">Across {spendingData.reduce((sum, item) => sum + item.count, 0)} transactions</div>
          </div>

          <div className="chart-container">
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={spendingData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={150}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {spendingData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.name] || COLORS['Other']} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value, entry) => {
                    const item = spendingData.find(d => d.name === value);
                    return `${value}: $${item?.value.toFixed(2) || 0}`;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="spending-breakdown">
            <h3>Category Breakdown</h3>
            <div className="breakdown-list">
              {spendingData
                .sort((a, b) => b.value - a.value)
                .map((item, index) => (
                  <div key={index} className="breakdown-item">
                    <div className="breakdown-info">
                      <span
                        className="category-color"
                        style={{ backgroundColor: COLORS[item.name] || COLORS['Other'] }}
                      ></span>
                      <span className="category-name">{item.name}</span>
                      <span className="category-count">({item.count} txns)</span>
                    </div>
                    <div className="breakdown-amount">
                      <span className="amount">${item.value.toFixed(2)}</span>
                      <span className="percentage">
                        {((item.value / totalSpent) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SpendingChart;
